import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, Images } from "lucide-react";
import { photoSrc } from "@/components/Avatar";
import { Linkify } from "@/components/messages/shared";
import { ShareCard } from "@/components/community/ShareToCommunity";

const urlOf = (im) => photoSrc(typeof im === "string" ? im : im.url);

/** Fotografia/álbum: imagem completa (contain) sobre fundo desfocado; álbuns deslizam na horizontal com indicador «n/N» e pontos. */
export function FeedImages({ images, testid }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const onScroll = () => { const el = ref.current; if (el) setIdx(Math.min(images.length - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)))); };
  return (
    <div className="absolute inset-0 bg-[#111]" data-testid={testid} data-index={idx}>
      <img src={urlOf(images[idx])} alt="" aria-hidden="true" style={{ position: "absolute", inset: "-8%", width: "116%", height: "116%", objectFit: "cover", filter: "blur(28px) brightness(0.75)" }} />
      <div ref={ref} onScroll={onScroll} className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory scrollbar-none" style={{ WebkitOverflowScrolling: "touch" }}>
        {images.map((im, i) => (
          <div key={i} className="w-full h-full shrink-0 snap-center snap-always flex items-center justify-center">
            <img src={urlOf(im)} alt="" loading={i === 0 ? "eager" : "lazy"} draggable={false} data-testid={`${testid}-${i}`}
                 style={{ width: "100%", height: "100%", objectFit: "contain" }} className="select-none" />
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <>
          <div className="absolute top-16 right-3 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur text-white text-xs font-semibold px-2.5 py-1" data-testid={`${testid}-counter`}>
            <Images size={12} /> {idx + 1}/{images.length}
          </div>
          <div className="absolute top-16 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-black/40 px-2 py-1.5 pointer-events-none" data-testid={`${testid}-dots`}>
            {images.map((_, i) => <span key={i} data-active={i === idx} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/45"}`} />)}
          </div>
        </>
      )}
    </div>
  );
}

/** Publicação só de texto: ecrã cheio com fundo da marca (coluna direita reservada às ações). */
export function FeedTextSlide({ item, testid }) {
  const long = (item.text || "").length > 280;
  return (
    <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 80% 0%, #c8102e 0%, #3a0710 45%, #0d0d0d 100%)" }} data-testid={testid}>
      <div className="auk-grain" style={{ position: "absolute", inset: 0 }} aria-hidden="true" />
      <div className="absolute inset-y-0 left-0 right-[4.5rem] flex flex-col items-center justify-center pl-5 pr-2 pb-36 pt-20 text-center">
        <Quote size={28} className="text-[#ffd400]/80 mb-5 shrink-0" aria-hidden="true" />
        <p className={`text-white font-display font-bold whitespace-pre-wrap break-words leading-snug max-h-full overflow-y-auto overscroll-contain ${long ? "text-base sm:text-lg" : "text-xl sm:text-2xl"}`} data-testid={`${testid}-text`}>
          <Linkify text={item.text} />
        </p>
        {item.share && <div className="mt-5 w-full max-w-sm text-left [&_a]:bg-white [&_a]:border-0" data-testid={`${testid}-share`}><ShareCard share={item.share} /></div>}
      </div>
    </div>
  );
}

/** Nome + origem + legenda com «Ver mais» (expande num painel com scroll próprio). */
export function FeedCaption({ item, testid }) {
  const [open, setOpen] = useState(false);
  const text = item.text || "";
  const hasMedia = item.video?.id || item.images?.length > 0;
  const s = item.source;
  const isCommunity = s?.type === "community";
  const canExpand = text.length > 90 || text.includes("\n");
  return (
    <div className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,.8)]" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        {item.system_tag ? <span className="font-bold text-[15px] truncate">{item.author?.name}</span>
          : <Link to={`/membro/${item.author?.id}`} className="font-bold text-[15px] truncate" data-testid={`${testid}-author`}>{item.author?.name}</Link>}
        {isCommunity && <Link to={s.link} className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-white/20 backdrop-blur-sm truncate max-w-[45%] shrink-0" data-testid={`${testid}-source`}>{s.name}</Link>}
      </div>
      {hasMedia && text && (
        <div className={`mt-1.5 ${open ? "max-h-[38vh] overflow-y-auto overscroll-contain rounded-xl bg-black/45 backdrop-blur-sm p-2.5 -ml-1" : ""}`} style={open ? { WebkitOverflowScrolling: "touch", touchAction: "pan-y" } : undefined}>
          <p className={`text-sm leading-snug whitespace-pre-wrap break-words ${open ? "" : "line-clamp-2"}`} data-testid={`${testid}-text`} data-expanded={open}><Linkify text={text} /></p>
          {canExpand && <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`} className="text-sm text-white/75 font-semibold mt-0.5 min-h-[32px]">{open ? "Ver menos" : "Ver mais"}</button>}
        </div>
      )}
      {hasMedia && item.share && <div className="mt-2 max-w-full [&_a]:bg-white/95 [&_a]:border-0" data-testid={`${testid}-share`}><ShareCard share={item.share} /></div>}
    </div>
  );
}
